import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { OrderModel } from '@/types/model/Order'
import { CreditCard, Banknote, Tag, Pencil, Trash2 } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { StatusBadge, TextInput } from '@/components/atoms'
import { DataType } from '@/utils'

import { CouponSection } from './CouponSection'
import { useBusiness } from '@/contexts/BusinessContext'

interface OrderPaymentInfoProps {
    order: OrderModel.Order
    actions: {
        updateDiscount: (discount: number) => void
        addCoupon: (code: string) => void
        removeCoupon: () => void
    }
    isEditable?: boolean
    loading?: boolean
}

interface DiscountFormValues {
    discount: number
}

export const OrderPaymentInfo = ({ order, actions, isEditable = true, loading = false }: OrderPaymentInfoProps) => {
    const { formatCurrency, currencySymbol } = useBusiness()
    const [isOpenDiscount, setIsOpenDiscount] = useState(false)

    const form = useForm<DiscountFormValues>({
        defaultValues: {
            discount: order.discount,
        },
    })

    useEffect(() => {
        if (isOpenDiscount) {
            form.reset({
                discount: order.discount,
            })
        }
    }, [isOpenDiscount, order.discount, form])

    const onDiscountSubmit = (data: DiscountFormValues) => {
        actions.updateDiscount(DataType.number(data.discount))
        setIsOpenDiscount(false)
    }

    const getPaymentIcon = () => {
        if (order.payment_method === 'cod') return <Banknote className="size-4 text-text-2" />
        return <CreditCard className="size-4 text-text-2" />
    }

    return (
        <Card>
            <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
                <CardTitle>Payment</CardTitle>
                <StatusBadge type="payment" status={order.payment_status} />
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-text-2">Method</span>
                    <span className="flex items-center gap-1.5 font-medium uppercase text-ink">
                        {getPaymentIcon()}
                        {order.payment_method}
                    </span>
                </div>
                {order.payment_option && (
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-text-2">Gateway</span>
                        <Badge className="capitalize">{order.payment_option}</Badge>
                    </div>
                )}
            </CardContent>

            <div className="space-y-2 border-t border-border-subtle p-4 text-sm">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-text-2">Subtotal</span>
                    <span className="tabular-nums text-ink">{formatCurrency(order.subtotal)}</span>
                </div>

                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1">
                        <span className="text-text-2">Discount</span>
                        {isEditable && (
                            <Popover open={isOpenDiscount} onOpenChange={setIsOpenDiscount}>
                                <PopoverTrigger asChild>
                                    <Button variant="ghost" size="icon-sm" aria-label="Edit order discount">
                                        <Pencil />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent align="start" className="w-64 p-4">
                                    <p className="mb-3 text-sm font-semibold text-ink">Order discount</p>
                                    <div className="space-y-3">
                                        <TextInput
                                            control={form.control}
                                            name="discount"
                                            label={`Amount (${currencySymbol})`}
                                            type="number"
                                            placeholder="0.00"
                                            errors={form.formState.errors}
                                        />
                                        <Button className="w-full" onClick={form.handleSubmit(onDiscountSubmit)}>
                                            Apply
                                        </Button>
                                    </div>
                                </PopoverContent>
                            </Popover>
                        )}
                    </div>
                    <span className="tabular-nums text-ink">- {formatCurrency(order.discount)}</span>
                </div>

                {order.coupon_id && order.coupon_info && (
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-1">
                            <Tag className="size-3.5 shrink-0 text-text-2" />
                            <span className="truncate text-text-2">Coupon {order.coupon_info.coupon_code}</span>
                            {isEditable && (
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label="Remove coupon"
                                    className="text-muted-foreground hover:text-error"
                                    onClick={actions.removeCoupon}>
                                    <Trash2 />
                                </Button>
                            )}
                        </div>
                        <span className="tabular-nums text-ink">- {formatCurrency(order.coupon_info.coupon_discount_amount)}</span>
                    </div>
                )}

                {order.delivery_charge > 0 && (
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-text-2">Shipping</span>
                        <span className="tabular-nums text-ink">{formatCurrency(order.delivery_charge)}</span>
                    </div>
                )}

                <div className="flex items-center justify-between gap-2 pt-1">
                    <span className="font-semibold text-ink">Total</span>
                    <span className="font-semibold tabular-nums text-ink">{formatCurrency(order.total)}</span>
                </div>
            </div>

            {!order.coupon_id && isEditable && (
                <div className="space-y-1.5 border-t border-border-subtle p-4">
                    <p className="text-sm text-ink">Coupon code</p>
                    <CouponSection
                        loading={loading}
                        onApplyCoupon={actions.addCoupon}
                    />
                </div>
            )}
        </Card>
    )
}
